import { useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { selectContactInfo } from '../redux/contactSlice';
import { Box, Container, Grid, Typography, Link, Stack, Button } from '@mui/material';
import { Instagram as InstagramIcon, Facebook as FacebookIcon, YouTube as YouTubeIcon, Phone as PhoneIcon, Email as EmailIcon } from '@mui/icons-material';

function Footer() {
  const contactInfo = useSelector(selectContactInfo);

  const contactData = contactInfo ? {
    facebook: contactInfo.socialMedia?.facebook || '',
    instagram: contactInfo.socialMedia?.instagram || '',
    youtube: contactInfo.socialMedia?.youtube || '',
    address: contactInfo.address || '',
    phone: contactInfo.phone || '',
    email: contactInfo.email || '',
    mapUrl: contactInfo.mapUrl
  } : {
    facebook: '',
    instagram: '',
    youtube: '',
    address: '',
    phone: '',
    email: '',
    mapUrl: ''
  };

  const navLinks = [
    { title: 'Home', path: '/' },
    { title: 'About', path: '/about' },
    { title: 'History', path: '/history' },
    { title: 'What is Gymkhana', path: '/what-is-gymkhana' },
    { title: 'Events', path: '/events' },
    { title: 'Documents', path: '/regulations' },
    { title: 'Gallery', path: '/gallery' },
    { title: 'Contact', path: '/contact' },
  ];

  const headerStyle = {
    fontFamily: "'Playfair Display', serif",
    color: '#ffc400ff',
    fontSize: '1.2rem',
    fontWeight: 600,
    mb: 3,
    letterSpacing: '0.5px',
    userSelect: 'none'
  };

  const socialItemStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    mb: 2,
    textDecoration: 'none',
    color: 'inherit',
    transition: 'all 0.3s ease',
    '&:hover': { color: '#ffc400ff' }
  };

  const iconCircleStyle = {
    width: 32,
    height: 32,
    borderRadius: '50%',
    backgroundColor: 'rgba(255,255,255,0.05)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.9rem'
  };

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: '#0a192f',
        color: 'white',
        pt: 10,
        pb: 4,
        mt: 'auto',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: 'linear-gradient(90deg, #d4af37 0%, #FFD700 50%, #d4af37 100%)',
        }
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={2} justifyContent="space-between">
          {/* Branding Section */}
          <Grid item xs={12} md={3}>
            <Box sx={{ maxWidth: '260px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <img
                  src="/logo512.png"
                  alt="AAGC Logo"
                  style={{ width: 140, height: 65, objectFit: 'contain' }}
                />
                <img
                  src="/fia-logo.png"
                  alt="FIA Logo"
                  style={{ width: 80, height: 55, objectFit: 'contain' }}
                />
              </Box>
              <Typography variant="body2" sx={{ lineHeight: 1.7, mb: 1, fontSize: '0.85rem' }}>
                Asia Auto Gymkhana Championship. Empowering motorsport excellence across Asia.
              </Typography>
              <Typography variant="body2" sx={{ fontStyle: 'italic', opacity: 0.5, fontSize: '0.8rem', borderLeft: '2px solid #d4af37', pl: 1 }}>
                "Precision is the difference between winning and losing."
              </Typography>
            </Box>
          </Grid>

          {/* Quick Links Section */}
          <Grid item xs={12} sm={6} md={1.5}>
            <Typography sx={headerStyle}>Quick Links</Typography>
            <Box>
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  component={RouterLink}
                  to={link.path}
                  color="inherit"
                  sx={{
                    ml: .5,
                    mb: 1.2,
                    display: 'block',
                    fontSize: '0.9rem',
                    textDecoration: 'none',
                    transition: 'opacity 0.2s',
                    '&:hover': {
                      color: '#ffc400ff',
                      fontWeight: 'bold'
                    }
                  }}
                >
                  {link.title}
                </Link>
              ))}
            </Box>
          </Grid>

          {/* Connect Section */}
          <Grid item xs={12} sm={6} md={1.5}>
            <Typography sx={headerStyle}>Connect</Typography>
            <Box>
              {contactData.facebook && (
                <Box component="a" href={contactData.facebook} target="_blank" sx={socialItemStyle}>
                  <Box sx={iconCircleStyle}><FacebookIcon fontSize="inherit" /></Box>
                  <Typography variant="caption" sx={{ fontSize: '0.9rem' }}>Facebook</Typography>
                </Box>
              )}
              {contactData.instagram && (
                <Box component="a" href={contactData.instagram} target="_blank" sx={socialItemStyle}>
                  <Box sx={iconCircleStyle}><InstagramIcon fontSize="inherit" /></Box>
                  <Typography variant="caption" sx={{ fontSize: '0.9rem' }}>Instagram</Typography>
                </Box>
              )}
              {contactData.youtube && (
                <Box component="a" href={contactData.youtube} target="_blank" sx={socialItemStyle}>
                  <Box sx={iconCircleStyle}><YouTubeIcon fontSize="inherit" /></Box>
                  <Typography variant="caption" sx={{ fontSize: '0.9rem' }}>YouTube</Typography>
                </Box>
              )}
            </Box>
          </Grid>

          {/* Contact Section */}
          <Grid item xs={12} sm={6} md={2.5}>
            <Typography sx={headerStyle}>Contact Us</Typography>
            <Stack spacing={2.5} sx={{ mb: 4 }}>
              {contactData.phone && (
                <Box
                  component="a"
                  href={`tel:${contactData.phone}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    textDecoration: 'none',
                    color: 'inherit',
                    transition: 'color 0.2s',
                    '&:hover': { color: '#ffc400ff' }
                  }}
                >
                  <PhoneIcon sx={{ color: '#ffc400ff', fontSize: '1.1rem' }} />
                  <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>{contactData.phone}</Typography>
                </Box>
              )}
              {contactData.email && (
                <Box
                  component="a"
                  href={`mailto:${contactData.email}?cc=intimation@chronopulse.com`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    textDecoration: 'none',
                    color: 'inherit',
                    transition: 'color 0.2s',
                    '&:hover': { color: '#ffc400ff' }
                  }}
                >
                  <EmailIcon sx={{ color: '#ffc400ff', fontSize: '1.1rem' }} />
                  <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>{contactData.email}</Typography>
                </Box>
              )}
            </Stack>
            <Button
              component={RouterLink}
              to="/contact"
              variant="outlined"
              startIcon={<EditNoteIcon />}
              sx={{
                color: '#d4af37',
                borderColor: 'rgba(212,175,55,0.5)',
                borderRadius: '25px',
                px: 3,
                fontSize: '0.8rem',
                textTransform: 'none',
                '&:hover': {
                  borderColor: '#d4af37',
                  backgroundColor: 'rgba(212,175,55,0.05)'
                }
              }}
            >
              Write to us
            </Button>
          </Grid>

          {/* Location Section */}
          <Grid item xs={12} sm={6} md={2.5}>
            <Typography sx={headerStyle}>Location</Typography>
            <Box sx={{ maxWidth: '260px' }}>
              <Typography variant="body2" sx={{ mb: 1, fontSize: '0.9rem', lineHeight: 1.6 }}>
                {contactData.address}
              </Typography>
              {contactData.mapUrl && (
                <Box sx={{ width: '100%', height: '120px', borderRadius: '12px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }}>
                  <iframe
                    src={contactData.mapUrl}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    title="Gymkhana location map"
                    loading="lazy"
                  />
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Container>
      <Box sx={{ mt: 8, pt: 4, borderTop: '1px solid rgba(255,255,255,0.1)', textAlign: 'center' }}>
        <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
          &copy; {new Date().getFullYear()} Asia Auto Gymkhana Championship. All rights reserved.
        </Typography>
      </Box>
    </Box>
  );
}

export default Footer;
